import React, { useState, useCallback, useMemo } from "react";
import { mutate } from "swr";
import { toast } from "react-toastify";
import ReactSwitch from "react-switch";
import classnames from "classnames";
import {
    EyeIcon,
    HeartIcon,
    PencilIcon,
    TrashIcon,
} from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { Tooltip } from "@mui/material";

import storeVatRate, {
    setConfirmationActivateClick,
    setDataIsUpdatedFranchise,
    setFnDelete,
    setIsDeleteCheckClick,
    setUrlHistory,
    setVatRateId,
    setIsActionsClicked,
    setIsEdited,
    setActions,
    setVatRateData,
    setIsLoading,
} from "../store";
import { store } from "../../../../components/VerticalLayout/store";
import TimePastSvgIcon from "@components/Common/SvgIcons/TimePastSvgIcon";
import ErrorToast from "@components/Common/ErrorTost";
import ConsultSvgIcon from "@components/Common/SvgIcons/ConsultSvgIcon";
import {
    setActivationDeactivationMessageModalOpened,
    setMessage,
    setYesFunction,
} from "@components/Common/ConfirmationActivationDeactivationMessageModal/store";
import { useFetchGlobal } from "../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";

const API_BASE = process.env.REACT_APP_API_V2_URL;

type CustomIconsComponentProps = {
    e: any;
    setVatRate: (e: any) => void;
    setIsAddVatRateOriginHistoryClicked: (v: boolean) => void;
    isAddVatRateOriginHistoryClicked: boolean;
    data: any;
    franchise: any;
    setListLinks: (links: string[]) => void;
    typeDisplay: string;
    isAddVatRateClicked: boolean;
    setIsAddVatRateClicked: (v: boolean) => void;
    setLocalFilterActive: (v: boolean) => void;
    setDataState: (state: { id: string; state: boolean }) => void;
    setIsConsult: (v: boolean) => void;
    setIsModalOpened: (v: boolean) => void;
    isModalOpened: boolean;
};

const getAuthHeaders = () => ({
    "Content-Type": "application/json",
});

export function CustomIconsComponent({
    e,
    setVatRate,
    setIsAddVatRateOriginHistoryClicked,
    isAddVatRateOriginHistoryClicked,
    data,
    franchise,
    setListLinks,
    typeDisplay,
    isAddVatRateClicked,
    setIsAddVatRateClicked,
    setLocalFilterActive,
    setDataState,
    setIsConsult,
    setIsModalOpened,
    isModalOpened,
}: CustomIconsComponentProps) {
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, oneShop, shopID, operatorID } = useSnapshot(
        store
    );
    const { t } = useTranslation();
    const {
        dataFranchise,
        dataShops,
        isActionsClicked,
        isEdited,
        modify,
        dataModified,
        isLoading,
    } = useSnapshot(storeVatRate);

    const [vatRateActivationStatus, setVatRateActivationStatus] = useState(
        e.selectedRow.status
    );
    const uuidUser = localStorage.getItem("uuidUser");
    const rowId = e.selectedRow.Id;
    const isArchived = typeDisplay === "archived";
    const isDisabled = isArchived || isLoading;

    const cantActivateWithShop = useMemo(
        () => dataFranchise.some((x: any) => x.Id === rowId && x.archived),
        [dataFranchise, rowId]
    );

    const dataAssociate = useMemo(() => {
        if (!dataShops) return [];
        return dataShops.flatMap(
            (el: any) =>
                el.subRows?.filter((elt: any) => elt.Id === rowId) || []
        );
    }, [dataShops, rowId]);

    const apiCall = useCallback(async (url: string, options: RequestInit) => {
        const res: any = await fetchGlobal(url, options);
        mutate(url, res);
        if (res?.error) throw new Error("API error");
        return res;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEdit = useCallback(() => {
        const action = () => {
            setIsEdited(false);
            setIsAddVatRateClicked(true);
            setVatRate(e);
            setLocalFilterActive(false);
        };
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(action);
        } else {
            action();
        }
    }, [
        isEdited,
        isActionsClicked,
        e,
        setVatRate,
        setIsAddVatRateClicked,
        setLocalFilterActive,
    ]);

    const handleConsult = useCallback(() => {
        handleEdit();
        setIsConsult(true);
    }, [handleEdit, setIsConsult]);

    const handleHistory = useCallback(() => {
        const typeParam = oneShop ? "associate" : "all";
        const shopParam = oneShop ? `&shopIds=${shopID}` : "";
        const url = `${API_BASE}/settings/general/vatRate/origin_history?userId=${userID}&franchiseId=${franchiseID}&typeDisplay=${typeParam}&idVatRates=${rowId}${shopParam}`;

        const action = () => {
            setIsAddVatRateClicked(false);
            setIsEdited(false);
            setUrlHistory(url);
            setIsAddVatRateOriginHistoryClicked(
                !isAddVatRateOriginHistoryClicked
            );
            setVatRateId(rowId);
        };

        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(action);
        } else {
            setUrlHistory(url);
            setIsAddVatRateOriginHistoryClicked(true);
            setVatRateId(rowId);
        }
    }, [
        oneShop,
        shopID,
        userID,
        franchiseID,
        rowId,
        isEdited,
        isActionsClicked,
        isAddVatRateOriginHistoryClicked,
        setIsAddVatRateClicked,
        setIsAddVatRateOriginHistoryClicked,
    ]);

    const changeStatusAssoc = useCallback(
        async (obj: any) => {
            setIsLoading(true);
            try {
                await apiCall(
                    `${API_BASE}/settings/general/vatRateStatusAssocEdit`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(obj),
                    }
                );
                const msg = obj.data[0].status
                    ? "The vat rate is successfully activate"
                    : "The vat rate is successfully deactivate";
                showSuccessToast(t(msg));
                setDataIsUpdatedFranchise!(true);
                setIsEdited(false);
            } catch {
                ErrorToast(t("There's an error")!);
            } finally {
                setIsLoading(false);
            }
        },
        [apiCall, t]
    );

    const archiveVatRate = useCallback(async () => {
        setIsLoading(false);
        const isArchiving = typeDisplay === "notArchived";
        const archived = isArchiving;

        const baseParams = `userId=${userID}&archived=${archived}&franchiseId=${franchiseID}&idVatRate=${rowId}&operatorId=${operatorID}&uuidUser=${uuidUser}`;
        const api = franchise
            ? `${API_BASE}/settings/general/vatRateArchivedEdit?${baseParams}`
            : `${API_BASE}/settings/general/vatRateArchiveAssocEdit?${baseParams}&shopId=${shopID}`;

        const message = isArchiving
            ? "The vat rate is successfully archived"
            : "The vat rate is successfully restored";

        // Check for conflicts
        const hasConflict =
            (franchise || oneShop) &&
            isArchiving &&
            dataShops.some((el: any) =>
                el.subRows?.some(
                    (elx: any) =>
                        elx.Id === rowId &&
                        !elx.archived &&
                        (!oneShop || el.shopId !== shopID)
                )
            );

        if (hasConflict && !oneShop) {
            ErrorToast(
                t(
                    "Please note that you cannot archive an item assigned to stores."
                )!
            );
            return;
        }

        // Handle restoration with linked shops
        if (!isArchiving && franchise) {
            const linkedShops = dataShops.flatMap(
                (el: any) =>
                    el.subRows
                        ?.filter((elx: any) => elx.Id === rowId && elx.archived)
                        .map((elx: any) => elx.shopId) || []
            );
            if (linkedShops.length > 0) {
                const links = linkedShops.map(
                    (sid: string) =>
                        `${API_BASE}/settings/general/vatRateArchiveAssocEdit?userId=${userID}&archived=false&franchiseId=${franchiseID}&idVatRate=${rowId}&shopId=${sid}&operatorId=${operatorID}`
                );
                setListLinks([...links, api]);
                setConfirmationActivateClick(true);
                return;
            }
        }

        const executeArchive = async () => {
            try {
                await apiCall(api, {
                    headers: getAuthHeaders(),
                    method: "DELETE",
                    body: JSON.stringify(data),
                });
                showSuccessToast(t(message));
                setDataIsUpdatedFranchise!(true);
            } catch {
                ErrorToast(t("There's an error")!);
            } finally {
                setIsLoading(false);
            }
        };

        if (isArchiving) {
            setIsDeleteCheckClick(true);
            setFnDelete(executeArchive);
        } else {
            await executeArchive();
        }
    }, [
        typeDisplay,
        franchise,
        oneShop,
        dataShops,
        rowId,
        userID,
        franchiseID,
        shopID,
        operatorID,
        uuidUser,
        data,
        apiCall,
        t,
        setListLinks,
    ]);

    const favoriteTva = useCallback(async () => {
        toast.dismiss();
        setIsLoading(true);
        try {
            // Collecter toutes les TVA dans un seul tableau
            const allVatRates = dataModified.map((tva: any) => ({
                id: tva.Id,
                code: tva.code,
                value: tva.value,
                tag: tva.tag,
                status: tva.status,
                pays: tva.Pays.ids,
                archived: tva.archived,
                isFavorite: tva.Id === rowId,
                shopId: oneShop ? shopID : 0, // shopId dans chaque objet
            }));

            // Une seule requête avec toutes les données
            const body = {
                userId: userID,
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser,
                canReplace: false,
                data: allVatRates,
            };

            const result: any = await fetchGlobal(
                `${API_BASE}/settings/general/editVatRate`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                }
            );

            if (result?.error) throw new Error("API error");

            const msg =
                dataAssociate.length > 0 && !oneShop
                    ? "Franchise favorite state has been successfully activated"
                    : "Favorite state has been enabled successfully";
            showSuccessToast(t(msg));

            if (dataAssociate.length > 0 && !oneShop) {
                setIsModalOpened(!isModalOpened);
            }
            setDataIsUpdatedFranchise!(true);
        } catch {
            ErrorToast(t("There's an error")!);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dataModified,
        rowId,
        userID,
        shopID,
        franchiseID,
        operatorID,
        uuidUser,
        oneShop,
        dataAssociate,
        isModalOpened,
        t,
        setIsModalOpened,
    ]);

    const handleFavorite = useCallback(() => {
        setIsLoading(true);
        if (!e.selectedRow.status) {
            ErrorToast(
                t(
                    "Please note, you cannot activate the favorite state for a disabled tva"
                )
            );
            setIsLoading(false);
        } else if (!e.selectedRow.isFavorite) {
            setIsLoading(false);
            setIsAddVatRateClicked(false);
            setVatRate(e);
            setVatRateData(e);
            favoriteTva();
        }
    }, [e, t, favoriteTva, setVatRate, setIsAddVatRateClicked]);

    const handleStatusToggle = useCallback(() => {
        if (e.selectedRow.isFavorite) {
            ErrorToast(t("Please note, you cannot deactivate a favorite VAT"));
            return;
        }
        setMessage(
            vatRateActivationStatus
                ? t(
                      "Caution, the VAT rate may be used by other modules.\n Would you like to confirm the deactivation of the selected VAT rate?"
                  )
                : t(
                      "Would you like to confirm the activation of the selected VAT rate?"
                  )
        );
        setYesFunction(() => {
            setVatRateActivationStatus(!vatRateActivationStatus);
            changeStatusAssoc({
                userId: userID,
                operatorId: operatorID,
                uuidUser,
                franchiseId: franchiseID,
                shopId: shopID,
                data: [{ id: rowId, status: !vatRateActivationStatus }],
            });
            setDataState({ id: rowId, state: !vatRateActivationStatus });
        });
        setActivationDeactivationMessageModalOpened(true);
    }, [
        e.selectedRow.isFavorite,
        vatRateActivationStatus,
        userID,
        operatorID,
        uuidUser,
        franchiseID,
        shopID,
        rowId,
        t,
        changeStatusAssoc,
        setDataState,
    ]);

    const handleArchive = useCallback(() => {
        setIsLoading(true);
        if (e.selectedRow.isFavorite) {
            ErrorToast(t("Please note, you cannot archive a favorite VAT"));
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
        const action = () => {
            setIsEdited(false);
            setIsAddVatRateClicked(false);
            setVatRateId(rowId);
            archiveVatRate();
        };
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(action);
        } else {
            setIsAddVatRateClicked(false);
            setVatRateId(rowId);
            archiveVatRate();
        }
    }, [
        e.selectedRow.isFavorite,
        isEdited,
        isActionsClicked,
        rowId,
        t,
        archiveVatRate,
        setIsAddVatRateClicked,
    ]);

    const iconClass = (base: string, extra?: Record<string, boolean>) =>
        classnames(
            base,
            { "not-allowed-icon__clz": isDisabled, ...extra },
            oneShop ? "mr-2" : "ml-3"
        );

    return (
        <div className="d-flex justify-content-around" style={{ zIndex: 100 }}>
            {!modify && (
                <Tooltip title={t("To consult")}>
                    <div>
                        <ConsultSvgIcon
                            className="pointer__clz afa_icn_toConsult"
                            height={35}
                            width={35}
                            style={{
                                cursor: "pointer",
                                position: "relative",
                                bottom: "5px",
                            }}
                            fill="black"
                            onClick={handleConsult}
                        />
                    </div>
                </Tooltip>
            )}
            {modify && (
                <>
                    <Tooltip
                        title={
                            isArchived || e.selectedRow.isFavorite
                                ? ""
                                : t("Enable favorite state")
                        }
                    >
                        <div>
                            <HeartIcon
                                onClick={handleFavorite}
                                height={25}
                                width={25}
                                name="heart"
                                fill={
                                    e.selectedRow.isFavorite ? "red" : "black"
                                }
                                className={classnames(
                                    "mdv_icn_FRlike",
                                    {
                                        "not-allowed-icon__clz": isDisabled,
                                        pointer__clz: !e.selectedRow.isFavorite,
                                    },
                                    oneShop ? "mr-2" : ""
                                )}
                            />
                        </div>
                    </Tooltip>
                    <Tooltip title={isArchived ? "" : t("Edit")}>
                        <div>
                            <PencilIcon
                                onClick={handleEdit}
                                height={25}
                                width={25}
                                style={{ cursor: "pointer" }}
                                className={iconClass(
                                    "pointer__clz cmn_icn_FRedit"
                                )}
                            />
                        </div>
                    </Tooltip>
                    {oneShop && (
                        <Tooltip
                            title={
                                isArchived
                                    ? ""
                                    : vatRateActivationStatus
                                    ? t("Unactivate")
                                    : t("Activate")
                            }
                        >
                            <div>
                                <ReactSwitch
                                    uncheckedIcon={false}
                                    checkedIcon={false}
                                    handleDiameter={26}
                                    offColor="#f7b4b8"
                                    onColor="#c2eddd"
                                    offHandleColor="#E30613"
                                    onHandleColor="#34C38F"
                                    width={50}
                                    height={20}
                                    checked={vatRateActivationStatus}
                                    onChange={handleStatusToggle}
                                    className={iconClass(
                                        "pointer__clz cmn_icn_BTenableDisable"
                                    )}
                                />
                            </div>
                        </Tooltip>
                    )}
                    {isArchived ? (
                        <Tooltip title={t("Unarchive")}>
                            <div>
                                <EyeIcon
                                    onClick={() => {
                                        setVatRateId(rowId);
                                        archiveVatRate();
                                    }}
                                    height={25}
                                    width={25}
                                    fill="black"
                                    style={{ cursor: "pointer" }}
                                    className={classnames(
                                        "pointer__clz cmn_icn_FRunarchive",
                                        {
                                            "not-allowed-icon__clz":
                                                isAddVatRateClicked ||
                                                (oneShop &&
                                                    cantActivateWithShop) ||
                                                isLoading,
                                        },
                                        oneShop ? "mr-2" : "ml-3"
                                    )}
                                />
                            </div>
                        </Tooltip>
                    ) : (
                        <Tooltip title={t("Archive")}>
                            <div>
                                <TrashIcon
                                    onClick={handleArchive}
                                    height={25}
                                    width={25}
                                    fill="red"
                                    style={{ cursor: "pointer" }}
                                    className={classnames(
                                        "pointer__clz",
                                        {
                                            "not-allowed-icon__clz": isLoading,
                                            cmn_icn_FRarchived: !oneShop,
                                            cmn_icn_BTarchived: oneShop,
                                        },
                                        oneShop ? "mr-2" : "ml-3"
                                    )}
                                />
                            </div>
                        </Tooltip>
                    )}
                </>
            )}
            <Tooltip title={t("Consult history")}>
                <div>
                    <TimePastSvgIcon
                        height={25}
                        width={25}
                        fill="black"
                        onClick={handleHistory}
                        style={{ cursor: "pointer" }}
                        className={iconClass("pointer__clz cmn_icn_FRhistory")}
                    />
                </div>
            </Tooltip>
        </div>
    );
}
